import { useState, useEffect, useRef, useCallback } from "react";

/**
 * SSE hook for real-time document processing notifications.
 * Connects to /client/notifications/sse/stream and handles
 * DOCUMENT_PROCESSED events with auto-reconnect + exponential backoff.
 */
const useSSE = ({ clientToken, enabled, onEvent }) => {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);

  // Store all props in refs so `connect` never needs to be recreated
  const onEventRef = useRef(onEvent);
  const clientTokenRef = useRef(clientToken);
  const enabledRef = useRef(enabled);

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { clientTokenRef.current = clientToken; }, [clientToken]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const handleEvent = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      if (onEventRef.current) {
        onEventRef.current(data);
      }
    } catch (err) {
      // Ignore unparseable events (e.g. heartbeat comments that leak through)
    }
  }, []);

  const connect = useCallback(() => {
    const token = clientTokenRef.current;
    if (!token || !enabledRef.current) return;

    cleanup();

    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const sseUrl = `${baseUrl}/client/notifications/sse/stream?token=${encodeURIComponent(token)}`;

    try {
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        reconnectAttemptRef.current = 0;
      };

      // Catch unnamed events (default SSE message type)
      eventSource.onmessage = handleEvent;

      // Also catch common named event types the backend might use
      eventSource.addEventListener("notification", handleEvent);
      eventSource.addEventListener("document_processed", handleEvent);

      eventSource.onerror = () => {
        setIsConnected(false);
        eventSource.close();
        eventSourceRef.current = null;

        // Only reconnect if still enabled
        if (!enabledRef.current) return;

        // Reconnect with exponential backoff (max 30s)
        const delay = Math.min(
          1000 * Math.pow(2, reconnectAttemptRef.current),
          30000
        );
        reconnectAttemptRef.current += 1;

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      };
    } catch (err) {
      console.error("Failed to establish SSE connection:", err);
    }
  }, [cleanup, handleEvent]);

  // Connect/disconnect based on enabled + token
  useEffect(() => {
    if (enabled && clientToken) {
      connect();
    } else {
      cleanup();
    }
    return cleanup;
  }, [enabled, clientToken, connect, cleanup]);

  return { isConnected };
};

export default useSSE;
